import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { RELEASED, languageTag, locale, localizedPath, t } from './i18n';

/**
 * Sets the document title, meta description and canonical URL while the page is mounted.
 * `canonical` is a path without language prefix and defaults to the current one without its query
 * string, so ?tab=… and ?symbol=… variants point at one page; null removes the tag (404).
 */
export function usePageMeta({ title, description, canonical }: { title: string; description?: string; canonical?: string | null }): void {
  const { pathname } = useLocation();
  const canonicalPath = canonical === undefined ? pathname : canonical;
  useEffect(() => {
    document.title = title;
    if (description) {
      document.querySelector('meta[name="description"]')?.setAttribute('content', description);
    }
  }, [title, description]);
  useEffect(() => {
    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonicalPath === null) {
      link?.remove();
      return;
    }
    const href = window.location.origin + localizedPath(locale(), canonicalPath);
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.append(link);
    }
    link.href = href;
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', href);
  }, [canonicalPath]);
  useEffect(
    () => () => {
      document.title = t('landing.metaTitle');
    },
    [],
  );
}

/** hreflang alternates for the current page: English (also x-default) and every released language. */
export function useLanguageAlternates(): void {
  const { pathname } = useLocation();
  useEffect(() => {
    // Replaces the prerendered page's own set as well, which describes the page the visitor landed on.
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((link) => link.remove());
    const add = (hreflang: string, path: string) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = hreflang;
      link.href = window.location.origin + path;
      document.head.append(link);
    };
    for (const l of ['en', ...RELEASED] as const) add(languageTag(l), localizedPath(l, pathname));
    add('x-default', pathname);
  }, [pathname]);
}
